<template>
    <!-- Overlay backdrop (overlay mode only) -->
    <Teleport to="body">
        <div
            v-if="mode === 'overlay'"
            class="sidenav-overlay"
            :style="overlayStyle"
            @click="close"
        />
    </Teleport>

    <!-- Panel — teleported to <body> so its z-index is never trapped inside
    a parent stacking context (transform / opacity / etc.) -->
    <Teleport to="body">
        <ul
            ref="panelRef"
            class="sidenav"
            :class="[sideNavClass, `${side}-aligned`, { 'sidenav--open': isOpen }]"
            :style="panelStyle"
            :aria-hidden="!isOpen || undefined"
            role="navigation"
        >
            <div class="sidenav-body">
                <p v-if="title" class="title">
                    {{ title }}
                </p>

                <a class="sidenav-close" href="#!" @click.prevent="close">
                    <button type="button" class="close">×</button>
                </a>

                <slot />
            </div>
        </ul>
    </Teleport>
</template>

<script lang="ts">
/**
 * Sidenav.vue – Bootstrap 3 compatible sidenav panel
 * Reuses sidenav.css; no extra styles needed.
 *
 * Props
 * ─────
 *  modelValue  boolean          open/closed state (v-model)
 *  side        'left' | 'right' which edge to slide from  (default: 'left')
 *  mode        'overlay'|'push' overlay backdrop or push body (default: 'overlay')
 *  width       string           any CSS width value         (default: '280px')
 *  zIndex      number           panel z-index               (default: 1050)
 *  title       string           header label text           (default: '')
 *  bodyClass   string           class added to <body>       (default: 'sidenav-open')
 *
 * Emits
 * ─────
 *  update:modelValue  boolean   v-model sync
 *  open                         after panel finishes opening
 *  close                        after panel finishes closing
 *
 * Usage
 * ─────
 *  <Sidenav v-model="menuOpen" side="left" title="Seleziona">
 *    <!-- your toolbar / list items -->
 *  </Sidenav>
 *
 *  <button @click="menuOpen = true">☰ Menu</button>
 */
</script>

<script setup lang="ts">
import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from 'vue';
// ── Props & emits ──────────────────────────────────────────────────────────

const props = withDefaults(
    defineProps<{
        modelValue?: boolean;
        side?: Side;
        mode?: Mode;
        width?: string;
        zIndex?: number;
        title?: string;
        bodyClass?: string;
        sideNavClass?: string;
    }>(),
    {
        modelValue: false,
        side: 'left',
        mode: 'overlay',
        width: '100%',
        zIndex: 1050,
        title: '',
        bodyClass: 'sidenav-open',
        sideNavClass: '',
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'open'): void;
    (e: 'close'): void;
}>();

// ── Types ──────────────────────────────────────────────────────────────────

type Side = 'left' | 'right';
type Mode = 'overlay' | 'push';

// ── State ──────────────────────────────────────────────────────────────────

const panelRef = ref<HTMLElement | null>(null);
const isOpen = ref(props.modelValue);

// ── Computed styles ────────────────────────────────────────────────────────

const panelStyle = computed(() => ({
    width: props.width,
    zIndex: props.zIndex,
}));

/**
 * Overlay: visible when open, hidden when closed.
 * We drive display + opacity via inline style so the CSS transition still fires
 * (same technique as the TS class version).
 */
const overlayStyle = computed(() => ({
    display: isOpen.value ? 'block' : 'none',
    opacity: isOpen.value ? '1' : '0',
    zIndex: props.zIndex - 1,
}));

// ── Public methods (also exposed so parent refs can call them) ─────────────

function open(): void {
    if(isOpen.value) return;
    isOpen.value = true;
    emit('update:modelValue', true);
    applyBodySideEffects(true);

    panelRef.value?.addEventListener('transitionend', () => emit('open'), {
        once: true,
    });
}

function close(): void {
    if(!isOpen.value) return;
    isOpen.value = false;
    emit('update:modelValue', false);
    applyBodySideEffects(false);

    panelRef.value?.addEventListener('transitionend', () => emit('close'), {
        once: true,
    });
}

function toggle(): void {
    isOpen.value ? close() : open();
}

defineExpose({ open, close, toggle });

// ── Body side-effects (class + push margin) ────────────────────────────────

function applyBodySideEffects(opening: boolean): void {
    if(opening) {
        document.body.classList.add(props.bodyClass);
        if(props.mode === 'push') {
            document.body.style.transition = 'margin 0.3s cubic-bezier(0.4,0,0.2,1)';
            document.body.style[props.side === 'left' ? 'marginLeft' : 'marginRight'] = props.width;
        }
    } else {
        document.body.classList.remove(props.bodyClass);
        if(props.mode === 'push') {
            document.body.style[props.side === 'left' ? 'marginLeft' : 'marginRight'] = '';
        }
    }
}

// ── Watchers ───────────────────────────────────────────────────────────────

// Keep internal state in sync with v-model from parent
watch(
    () => props.modelValue,
    (val) => {
        val ? open() : close();
    },
);

// ── Keyboard handler ───────────────────────────────────────────────────────

function onKeydown(e: KeyboardEvent): void {
    if(e.key === 'Escape' && isOpen.value) close();
}

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
    // Reflect initial modelValue
    if(props.modelValue) applyBodySideEffects(true);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    // Clean up body state in case component is destroyed while open
    document.body.classList.remove(props.bodyClass);
    const prop = props.side === 'left' ? 'marginLeft' : 'marginRight';
    document.body.style[prop] = '';
});
</script>
